import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ServerOff } from 'lucide-react';
import { useGameStore } from '@/stores/game.store';
import { useActiveGameGroup, useUsableGameGroups } from '@/stores/gameGroups.store';
import { joinGroupLabels } from '@/utils/supervision';
import { missingCapability } from '@/config/panelModules';
import { useSupervisionLanding } from './supervisionNav';
import styles from './ModuleDisabled.module.scss';

/**
 * Garde de capacité (cf AdminLayout) : l'URL d'une page de Supervision que le satellite du groupe
 * actif ne sert pas a été ouverte directement (favori, lien partagé, changement de groupe sur la
 * page). Groupe « grades seuls » par exemple : pas de base, donc ni joueurs ni sanctions. La page
 * n'est pas montée, au lieu d'échouer sur chaque requête ; on dit pourquoi, quels autres groupes la
 * servent, et un lien mène à la première page disponible. Même présentation que ModuleDisabled.
 * Les groupes qui la servent sont lus avec la même règle que la garde (config/panelModules) : version
 * réduite comprise, comme la page Joueurs en fiche allégée d'un groupe qui n'annonce que profiles.
 */
export function CapabilityMissing() {
  const { pathname } = useLocation();
  const activeGameId = useGameStore((s) => s.activeGameId);
  const group = useActiveGameGroup();
  const usable = useUsableGameGroups(activeGameId);
  const landing = useSupervisionLanding();
  const label = group?.label ?? '';
  // Groupes qui servent la page, en version réduite comprise (Joueurs en fiche allégée avec profiles).
  // Capacités jamais sondées (liste vide) : non.
  const servedBy = usable.filter((g) => g.key !== group?.key && missingCapability(pathname, g.capabilities ?? []) === null);

  return (
    <div className={styles.wrap} role="status">
      <span className={styles.icon} aria-hidden><ServerOff size={22} /></span>
      <h1 className={styles.title}>Page indisponible pour {label}</h1>
      <p className={styles.text}>
        Cette page n'est pas disponible pour le groupe {label} : son satellite ne la sert pas.
      </p>
      <p className={styles.hint}>
        {servedBy.length > 0
          ? <>Elle l'est pour {joinGroupLabels(servedBy.map((g) => g.label))} : change de groupe dans le sélecteur.</>
          : "Aucun autre groupe de ton affectation ne la sert."}
      </p>
      {landing !== pathname && (
        <Link to={landing} className={styles.action}>
          <ArrowRight size={14} aria-hidden /> Ouvrir la première page disponible
        </Link>
      )}
    </div>
  );
}
